import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SelectOption = {
  value: string;
  label: string;
  avatar?: string;
  isCustom?: boolean;
  customContent?: React.ReactNode;
  additionalInfo?: React.ReactNode;
};

type CustomSelectProps = {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
  className?: string;
  value?: string;
  defaultSelectedKeys?: Iterable<string>;
  selectedKeys?: Iterable<string>;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSelectionChange?: (keys: Set<string>) => void;
  'aria-label'?: string;
  disabled?: boolean;
  name?: string;
};

export const CustomSelect = forwardRef<HTMLButtonElement, CustomSelectProps>(
  (
    {
      label,
      placeholder,
      options,
      error,
      className,
      value,
      defaultSelectedKeys,
      selectedKeys,
      onChange,
      onSelectionChange,
      disabled,
      name,
      ...props
    },
    ref,
  ) => {
    // Derive the controlled value from various HeroUI-compatible props
    const derivedValue =
      value ??
      (selectedKeys
        ? Array.from(selectedKeys)[0]
        : defaultSelectedKeys
          ? Array.from(defaultSelectedKeys)[0]
          : undefined);

    const handleValueChange = (newValue: string) => {
      // Support both onChange (react-hook-form style) and onSelectionChange (HeroUI style)
      if (onChange) {
        const syntheticEvent = {
          target: { value: newValue, name: name ?? '' },
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
      if (onSelectionChange) {
        onSelectionChange(new Set([newValue]));
      }
    };

    return (
      <div>
        {label && (
          <Label className="block text-sm font-medium text-muted-foreground mb-1">{label}</Label>
        )}
        <Select
          value={derivedValue}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            ref={ref}
            className={cn(
              'min-h-[44px]',
              error && 'border-red-500 focus:ring-red-500',
              className,
            )}
            aria-label={props['aria-label'] || label || placeholder || 'Select option'}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.isCustom && option.customContent ? (
                  option.customContent
                ) : (
                  <div className="flex items-center gap-2.5">
                    <span className="font-medium">{option.label}</span>
                    {option.avatar && (
                      <img
                        src={option.avatar}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    )}
                  </div>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  },
);

CustomSelect.displayName = 'CustomSelect';
